import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TAvatar } from "@/types/TAvatar";
import type { TLiveAvatarOption, TLiveVoiceOption } from "@/types/TLiveAvatarOption";
import { useAvatarSelect } from "@/hooks/dashboard/useAvatarSelect";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { fetchAvailableAvatars } from "@/services/heygen/dashboard/fetchAvailableAvatars";
import { fetchAvailableVoices } from "@/services/heygen/dashboard/fetchAvailableVoices";
import { Loading } from "@/components/ui/loading";

interface AvatarFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  avatar?: TAvatar | null;
}

export function AvatarFormDialog({
  open,
  onOpenChange,
  avatar,
}: AvatarFormDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [weight, setWeight] = useState(1.0);
  const [avatarPopoverOpen, setAvatarPopoverOpen] = useState(false);
  const [voicePopoverOpen, setVoicePopoverOpen] = useState(false);
  const { addAvatar, updateAvatar, avatars } = useAvatarSelect();

  const { data: availableAvatars, isLoading: isLoadingAvatars } = useQuery<TLiveAvatarOption[]>({
    queryKey: ["available-avatars"],
    queryFn: fetchAvailableAvatars,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: availableVoices, isLoading: isLoadingVoices } = useQuery<TLiveVoiceOption[]>({
    queryKey: ["available-voices"],
    queryFn: fetchAvailableVoices,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const calculateActualPercentage = () => {
    const otherAvatars = avatars.filter(a => a.avatar_id !== avatar?.avatar_id);
    const totalWeight = otherAvatars.reduce((sum, a) => sum + (a.weight ?? 1.0), 0) + weight;
    return totalWeight > 0 ? (weight / totalWeight) * 100 : 0;
  };

  const isEditMode = !!avatar;

  useEffect(() => {
    if (open) {
      setName(avatar?.avatar_name || "");
      setId(avatar?.avatar_id || "");
      setVoiceId(avatar?.voice_id || "");
      setPreviewUrl(avatar?.preview_image_url || "");
      setWeight(avatar?.weight ?? 1.0);
    }
  }, [avatar, open]);

  const handleAvatarSelect = (avatarId: string) => {
    setId(avatarId);
    setAvatarPopoverOpen(false);
    const selected = availableAvatars?.find(a => a.id === avatarId);
    if (selected) {
      setName(selected.name);
      if (selected.preview_url) {
        setPreviewUrl(selected.preview_url);
      }
      if (selected.default_voice) {
        setVoiceId(selected.default_voice.id);
      }
    }
  };

  const handleVoiceSelect = (selectedVoiceId: string) => {
    setVoiceId(selectedVoiceId);
    setVoicePopoverOpen(false);
  };

  const handleSubmit = () => {
    const resolvedPreviewUrl = previewUrl || selectedAvatarOption?.preview_url || "";

    const newAvatarData: TAvatar = {
      avatar_name: name,
      avatar_id: id,
      voice_id: voiceId,
      preview_image_url: resolvedPreviewUrl,
      preview_low_res_image_url: resolvedPreviewUrl,
      weight: weight,
    };

    if (isEditMode && avatar) {
      updateAvatar({ ...avatar, ...newAvatarData });
    } else {
      addAvatar(newAvatarData);
    }
    onOpenChange(false);
  };

  const selectedAvatarOption = availableAvatars?.find(a => a.id === id);
  const selectedVoiceOption = availableVoices?.find(v => v.id === voiceId);

  const customAvatars = availableAvatars?.filter(a => a.source === "custom") ?? [];
  const publicAvatars = availableAvatars?.filter(a => a.source === "public") ?? [];

  const avatarDisplayLabel = selectedAvatarOption
    ? `${selectedAvatarOption.name} (${selectedAvatarOption.source})`
    : id || t("dialogs.selectAvatarPlaceholder");

  const voiceDisplayLabel = selectedVoiceOption
    ? `${selectedVoiceOption.name} (${selectedVoiceOption.language})`
    : voiceId || t("dialogs.selectVoicePlaceholder");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? t("dialogs.editAvatar") : t("dialogs.addNewAvatar")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 pb-4">
          {/* Avatar selector */}
          <div className="space-y-2">
            <Label>{t("dialogs.selectAvatar")}</Label>
            {isLoadingAvatars ? (
              <Loading inline spinnerSize="h-4 w-4" messageKey="common.loading" />
            ) : (
              <Popover modal={true} open={avatarPopoverOpen} onOpenChange={setAvatarPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={avatarPopoverOpen}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">{avatarDisplayLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput placeholder={t("dialogs.searchAvatar")} />
                    <CommandList>
                      <CommandEmpty>{t("dialogs.noAvatarsFound")}</CommandEmpty>
                      {customAvatars.length > 0 && (
                        <CommandGroup heading={t("dialogs.customAvatars")}>
                          {customAvatars.map((a) => (
                            <CommandItem
                              key={a.id}
                              value={`${a.name} custom`}
                              onSelect={() => handleAvatarSelect(a.id)}
                            >
                              <Check className={cn("mr-2 h-4 w-4", id === a.id ? "opacity-100" : "opacity-0")} />
                              <div className="flex items-center gap-2">
                                {a.preview_url && (
                                  <img src={a.preview_url} alt={a.name} className="w-6 h-6 rounded-full object-cover" />
                                )}
                                <span>{a.name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                      {publicAvatars.length > 0 && (
                        <CommandGroup heading={t("dialogs.publicAvatars")}>
                          {publicAvatars.map((a) => (
                            <CommandItem
                              key={a.id}
                              value={`${a.name} public`}
                              onSelect={() => handleAvatarSelect(a.id)}
                            >
                              <Check className={cn("mr-2 h-4 w-4", id === a.id ? "opacity-100" : "opacity-0")} />
                              <div className="flex items-center gap-2">
                                {a.preview_url && (
                                  <img src={a.preview_url} alt={a.name} className="w-6 h-6 rounded-full object-cover" />
                                )}
                                <span>{a.name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            {(selectedAvatarOption?.preview_url || previewUrl) && (
              <img
                src={selectedAvatarOption?.preview_url || previewUrl}
                alt={selectedAvatarOption?.name || name}
                className="w-full max-h-32 object-contain rounded-md border"
              />
            )}
          </div>

          {/* Avatar name */}
          <div className="space-y-2">
            <Label htmlFor="avatar-name">{t("dialogs.avatarName")}</Label>
            <Input
              id="avatar-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("dialogs.enterAvatarName")}
            />
          </div>

          {/* Voice selector */}
          <div className="space-y-2">
            <Label>{t("dialogs.selectVoice")}</Label>
            {isLoadingVoices ? (
              <Loading inline spinnerSize="h-4 w-4" messageKey="common.loading" />
            ) : (
              <Popover modal={true} open={voicePopoverOpen} onOpenChange={setVoicePopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={voicePopoverOpen}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">{voiceDisplayLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput placeholder={t("dialogs.searchVoice")} />
                    <CommandList>
                      <CommandEmpty>{t("dialogs.noVoicesFound")}</CommandEmpty>
                      <CommandGroup>
                        {availableVoices?.map((v) => (
                          <CommandItem
                            key={v.id}
                            value={`${v.name} ${v.language} ${v.gender}`}
                            onSelect={() => handleVoiceSelect(v.id)}
                          >
                            <Check className={cn("mr-2 h-4 w-4", voiceId === v.id ? "opacity-100" : "opacity-0")} />
                            <span>{v.name}</span>
                            <span className="text-muted-foreground ml-2 text-xs">
                              ({v.language}, {v.gender})
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
            {selectedVoiceOption && (
              <p className="text-xs text-muted-foreground">
                {selectedVoiceOption.name} &middot; {selectedVoiceOption.language} &middot; {selectedVoiceOption.gender}
              </p>
            )}
          </div>

          {/* Weight slider */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="avatar-weight">
                {t("dialogs.avatarWeight")}
              </Label>
              <span className="text-sm font-medium">
                {t("dialogs.willAppear")}: {calculateActualPercentage().toFixed(1)}%
              </span>
            </div>
            <Slider
              id="avatar-weight"
              min={0}
              max={1}
              step={0.01}
              value={[weight]}
              onValueChange={(value) => setWeight(value[0])}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t("dialogs.never")} (0%)</span>
              <span>{t("dialogs.relativeWeight")}: {(weight * 100).toFixed(0)}</span>
              <span>{t("dialogs.maximum")} (100%)</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {t("dialogs.avatarWeightDescriptionNew")}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("dialogs.cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={!id}>
            {t("common.apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
